import { useCallback, useEffect, useRef, useState } from 'react';
import {
  cloneFileOverrides,
  fileOverridesEqual,
  setBlockFiles,
} from '../../../DocEditor/utils/fileOverrides';
import { shouldApplyHostOverrides } from './missionOverrideSync';

/**
 * Per-mission file-upload values for one attached doc.
 *
 * SprintDetail is a single mounted instance across campaign tabs, and this
 * pane stays mounted when the sheet stays open. `docId` alone is not a
 * reliable seed key: the host can briefly pass the previous mission (or
 * empty overrides) then the real `task.docFiles`. Accept those host updates,
 * but ignore stale props while we're waiting for our own write to echo back.
 */
export function useLibraryDocFiles({ fileOverrides, onFilesChange, docId, missionId }) {
  const [localFileOverrides, setLocalFileOverrides] = useState(() =>
    cloneFileOverrides(fileOverrides)
  );
  const onFilesChangeRef = useRef(onFilesChange);
  onFilesChangeRef.current = onFilesChange;
  const localRef = useRef(localFileOverrides);
  localRef.current = localFileOverrides;
  const lastPushedRef = useRef(cloneFileOverrides(fileOverrides));
  const identityRef = useRef({ docId, missionId });
  const pendingEchoRef = useRef(false);

  useEffect(() => {
    const next = cloneFileOverrides(fileOverrides);
    const identityChanged =
      identityRef.current.docId !== docId ||
      identityRef.current.missionId !== missionId;
    identityRef.current = { docId, missionId };

    const decision = shouldApplyHostOverrides({
      identityChanged,
      incomingEqualsLastPushed: fileOverridesEqual(next, lastPushedRef.current),
      incomingEqualsLocal: fileOverridesEqual(next, localRef.current),
      pendingEcho: pendingEchoRef.current,
    });
    if (decision.clearPending) pendingEchoRef.current = false;
    if (!decision.apply) return;

    localRef.current = next;
    lastPushedRef.current = next;
    setLocalFileOverrides(next);
  }, [docId, missionId, fileOverrides]);

  const handleFilesChange = useCallback((blockId, files) => {
    const next = setBlockFiles(localRef.current, blockId, files);
    localRef.current = next;
    lastPushedRef.current = next;
    pendingEchoRef.current = true;
    setLocalFileOverrides(next);
    onFilesChangeRef.current?.(next);
  }, []);

  return { localFileOverrides, handleFilesChange };
}
